"use strict";

import InteractionView from "/js/interaction-views/interaction-view.js";
import Util from "/js/util/util.js";
import AppUtil from "/js/util/app-util.js";
import DragDelegate from "/js/util/drag-delegate.js";
import FadeUtil from "/js/util/fade-util.js";
import FocusUtil from '/js/util/focus-util.js';
import Constants from '/js/core/constants.js';

/**
 * 
 */
export default class ThisThatInteractionView extends InteractionView {

  #$boundingBox;
  #$leftHolder;
  #$rightHolder;
  #$leftImage;
  #$rightImage;
  #$overlayImage;
  #$button;

  #dragDelegate;

  #boundingWidth = 0;
  #boundingHeight = 0;
  #imageWidth = 0;
  #span = 0;
  #carouselValue = 0;
  #dragStartCarouselValue = 0;
  #isButtonShowing = false;

  constructor(data) {
    super(data);
    const s = `
        <div class="title">${ data.subCaption }</div>
        <div id="this-that-bounding-box">
          <div id="this-that-left-holder"><div id="this-that-left-image" tabindex="${ Constants.TABINDEX_CONTENT_GENERAL }"></div></div>
          <div id="this-that-right-holder"><div id="this-that-right-image" tabindex="${ Constants.TABINDEX_CONTENT_GENERAL + 1 }"></div></div>
          <div id="this-that-overlay"></div>
        </div>
        <div id="this-that-button" tabindex="${Constants.TABINDEX_CONTENT_GENERAL + 2}" class="interaction-big-button">${ data.confirmButtonText }</div>
    `;
    this.$el.append($(s));

    this.#$boundingBox = this.$el.find("#this-that-bounding-box");
    this.#$leftHolder = this.$el.find("#this-that-left-holder");
    this.#$rightHolder = this.$el.find("#this-that-right-holder");
    this.#$leftImage = this.$el.find("#this-that-left-image");
    this.#$rightImage = this.$el.find("#this-that-right-image");
    this.#$overlayImage = this.$el.find("#this-that-overlay");
    this.#$button = this.$el.find("#this-that-button");

    let url = AppUtil.makeLessonAssetPath(this.data.images[0].image);
    this.#$leftImage.css('background-image', `url("${url}")`);

    url = AppUtil.makeLessonAssetPath(this.data.images[1].image);
    this.#$rightImage.css('background-image', `url("${url}")`);

    url = AppUtil.makeLessonAssetPath(this.data.coverImage);
    this.#$overlayImage.css('background-image', `url("${url}")`);

    this.#$leftImage.on("click tap", $.proxy(this.#onLeftImageClick, this));
    this.#$rightImage.on("click tap", $.proxy(this.#onRightImageClick, this));
    FocusUtil.doStandardTreatment(this.#$leftImage);
    FocusUtil.doStandardTreatment(this.#$rightImage);

    this.#$button.on("click tap", $.proxy(this.#onButton, this));
    FocusUtil.doStandardTreatment(this.#$button);

    let desc = this.data.images[0].accessibilityDescription;
    if (desc) {
      this.#$leftImage.attr("role", "img");
      this.#$leftImage.attr("aria-label", desc);
    }
    desc = this.data.images[1].accessibilityDescription;
    if (desc) {
      this.#$rightImage.attr("role", "img");
      this.#$rightImage.attr("aria-label", desc);
    }

    this.#dragDelegate = new DragDelegate(this, this.#$boundingBox);
    this.#dragDelegate.setCallbacks(
        this.#onDelegateDownTT, this.#onDelegateMoveTT, this.#onDelegateUpTT);

    this.resize();
  }

  resize() {
    this.#boundingWidth = this.#$boundingBox.width();
    this.#boundingHeight = this.#$boundingBox.height();

    const top = this.data.imageStartY * this.#boundingHeight;
    const bottom = this.data.imageEndY * this.#boundingHeight;
    const imageH = bottom - top;
    const ar = 1 / this.data.imageAspectRatio; // bc "imageAspectRatio" is h/w
    const imageW = imageH * ar;

    // Holder y position.
    const holderY = top + imageH / 2;
    this.#$leftHolder.css("top", holderY + "px");
    this.#$rightHolder.css("top", holderY + "px");

    // Image dimensions.
    this.#$leftImage.width(imageW);
    this.#$leftImage.height(imageH);
    this.#$rightImage.width(imageW);
    this.#$rightImage.height(imageH);

    // Image position within its holder.
    const imageY = imageH / -2;
    this.#$leftImage.css("top", imageY);
    this.#$rightImage.css("top", imageY);
    const imageX = imageW / -2;
    this.#$leftImage.css("left", imageX);
    this.#$rightImage.css("left", imageX);

    this.#imageWidth = imageW;
    const extraMargin = this.#boundingHeight * 0.05;
    this.#span = this.#imageWidth + extraMargin;

    this.#setCarouselValue(this.#carouselValue);

    // Note, overlay image does not need to be resized programmatically.
  }

  #setCarouselValue(value) {
    // value's normal range is -1 to +1.
    this.#carouselValue = value;
    let f = value * 0.5;
    this.#updateCarouselImageValue(false, this.#carouselValue);
    this.#updateCarouselImageValue(true, this.#carouselValue);

    if (!this.#isButtonShowing && Math.abs(this.#carouselValue) > 0.8) {
      this.#isButtonShowing = true;
      FadeUtil.fadeIn(this.#$button);
    }
  }

  #updateCarouselImageValue(isRightImage, value) {

    const $o = isRightImage ? this.#$rightHolder : this.#$leftHolder;

    // val's normal range is also -1 to +1.
    let val = Util.map(-value, -1, 1,  -0.5, 0.5);
    val += isRightImage ? 0.5 : -0.5;

    // X position.
    let x = this.#boundingWidth / 2;
    x += this.#carouselValueToPixels(val);
    $o.css("left", x + "px");

    // Scale follows the top arc of a cosine curve.
    // (Note how we're still just treating x as linear though).
    let deg = Util.map(val, -1, 1, -30, +30);
    let rad = deg * (Math.PI / 180);
    const scale = Math.cos(rad);
    Util.setScale($o, scale);

    // Opacity also follows a cosine curve.
    deg = Util.map(val, -1, 1, -45, +45);
    rad = deg * (Math.PI / 180);
    const opacity = Math.cos(rad);
    $o.css("opacity", opacity);
  }

  #animateCarouselValue(targetValue) {
    // TODO: Replace with css property transition.
      $({ n: this.#carouselValue }).animate({ n: targetValue}, {
        duration: 333,
        easing: "easeOutCubic",
        step: value => this.#setCarouselValue(value)
      });
  }

  #onDelegateDownTT(x, y) {
    this.#dragStartCarouselValue = this.#carouselValue;
  }

  #onDelegateMoveTT(deltaX, deltaY) {
    const deltaValue = this.#pixelsToCarouselValue(deltaX);
    let val = this.#dragStartCarouselValue + (deltaValue * -1);
    val = Util.clamp(val, -1.15, 1.15);  // Note the extra play allowed here.
    this.#setCarouselValue(val);
  }

  #onDelegateUpTT(x, y) {

    let val;
    if (Math.abs(this.#carouselValue) < 0.5 && !this.#isButtonShowing) {
      // Snap back to middle.
      val = 0;
    } else {
      // Snap to an image.
      val = this.#carouselValue < 0 ? -1 : +1;
    }
    this.#animateCarouselValue(val);
  }

  #onLeftImageClick(e) {
    cl('leftimageclick')
    e.stopImmediatePropagation();
    this.#animateCarouselValue(-1);
  }

  #onRightImageClick(e) {
    cl('rightimageclick')
    e.stopImmediatePropagation();
    this.#animateCarouselValue(1);
  }

  #onButton(e) {
    const index = this.#carouselValue < 0 ? 0 : 1;
    const imageObject = this.data.images[index];
    const key = imageObject.resultKey;
    this.answers = [ { resultKey: key } ];
    this.finish();
  }

  #carouselValueToPixels(value) {
    return this.#span * value;
  }

  #pixelsToCarouselValue(pixels) {
    return (pixels * 2) / this.#span;
  }

  finish() {
    super.finish();
    this.#dragDelegate.clearDragHandlers();
    // Also clear down handler
    this.#$boundingBox.off("mousedown touchstart");
  }
}
